import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  trackDemoCta,
  trackDemoOpen,
  trackDemoFirstInteraction,
  trackDemoInteraction,
} from '../../../common/analytics';
import {
  Frame,
  Placeholder,
  TryButton,
  FrameNote,
  Mobile,
  PreviewStage,
  Caption,
  DesktopHint,
  frameDelay,
} from './demoembed.style';

// The file name is explicit: CloudFront serves no directory index for sub-paths (see demo/README.md).
export const DEMO_SRC = '/demo-app/index.html';

const PREVIEW = [
  { file: 'step1-profile', alt: 'Choosing the acme-prod profile in the left menu', caption: 'Pick a profile' },
  { file: 'step2-billing', alt: 'Billing view with last month at about 8,000 dollars', caption: "Review last month's bill" },
  { file: 'step3-details', alt: 'A service row expanded to show its cost details by usage type', caption: 'Open a service for its cost details' },
  { file: 'step4-scan', alt: 'Savings Scan progress panel with services being checked', caption: 'Run a Savings Scan' },
  { file: 'step5-results', alt: 'Scan summary with the potential monthly savings per service', caption: 'Read the results' },
  { file: 'step6-flow', alt: 'Savings flow diagram with one path highlighted', caption: 'Click the savings flow' },
  { file: 'step7-evidence', alt: 'An opportunity opened to show its evidence and recommended action', caption: 'Open the evidence' },
];

// Loads the demo bundle only after the visitor scrolls the frame into view or presses the
// button, so the home page itself never downloads it up front.
const WIDE = '(min-width: 768px)';

// At most this many distinct elements are counted per visit (a click-happy visitor must not flood GA4).
const MAX_INTERACTIONS = 25;

export const DemoFrame = ({ eager, tall, location }) => {
  const [active, setActive] = useState(false);
  const ref = useRef(null);
  const iframeRef = useRef(null);
  const trigger = useRef('');

  // Phones get the animated preview instead; the iframe is never created there, even for /demo.
  useEffect(() => {
    if (eager && window.matchMedia(WIDE).matches) {
      trigger.current = 'page_load';
      setActive(true);
    }
  }, [eager]);

  useEffect(() => {
    if (active || typeof IntersectionObserver === 'undefined') return undefined;
    if (!window.matchMedia(WIDE).matches) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trigger.current = trigger.current || 'scroll';
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [active]);

  // Once per activation of the iframe: the demo was opened, and what the visitor then clicks.
  useEffect(() => {
    if (!active) return undefined;
    trackDemoOpen(location, trigger.current || 'scroll');
    const seen = new Set();
    const onMessage = (event) => {
      // Only our own demo frame, from our own origin; anything else is ignored.
      if (event.origin !== window.location.origin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const data = event.data;
      if (!data || data.source !== 'cloudpouch-demo' || data.type !== 'interaction') return;
      const target = String(data.target || 'other').slice(0, 40);
      const step = String(data.step || '').slice(0, 40);
      if (seen.size === 0) trackDemoFirstInteraction(location, target, step);
      if (seen.has(target) || seen.size >= MAX_INTERACTIONS) return;
      seen.add(target);
      trackDemoInteraction(location, target, step);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [active, location]);

  return (
    <Frame ref={ref} $tall={tall} data-demo-frame data-demo-active={active ? 'true' : 'false'}>
      {active ? (
        <iframe
          ref={iframeRef}
          title="CloudPouch 2.0 interactive demo on sample data"
          src={DEMO_SRC}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <Placeholder>
          <p>The demo loads when this frame scrolls into view.</p>
          <TryButton
            type="button"
            onClick={() => {
              trigger.current = 'button';
              setActive(true);
            }}
          >
            Try the interactive demo
          </TryButton>
        </Placeholder>
      )}
    </Frame>
  );
};

DemoFrame.propTypes = { eager: PropTypes.bool, tall: PropTypes.bool, location: PropTypes.string };
DemoFrame.defaultProps = { eager: false, tall: false, location: 'home' };

export const DemoFrameNote = () => (
  <FrameNote>
    Sample data for fictional accounts, shown on the Pro plan.{' '}
    <a href="/demo/" onClick={() => trackDemoCta('demo-embed-full-size')}>
      Open the demo in full size
    </a>
  </FrameNote>
);

// Small screens get an animated preview of the same scenario (frames recorded from the demo).
export const DemoMobilePreview = () => (
  <Mobile>
    <PreviewStage role="img" aria-label="Preview of the demo: profile, billing, cost details, scan, results, savings flow and evidence">
      {PREVIEW.map((step, index) => (
        <img
          key={step.file}
          src={`/demo-preview/${step.file}.webp`}
          alt=""
          loading="lazy"
          decoding="async"
          style={{ animationDelay: frameDelay(index) }}
        />
      ))}
    </PreviewStage>
    <Caption>
      {PREVIEW.map((step, index) => (
        <li key={step.file}>
          <span>{index + 1}</span>
          {step.caption}
        </li>
      ))}
    </Caption>
    <DesktopHint>
      <strong>Try it on desktop</strong>
      The interactive demo runs on a tablet or computer. Open cloudpouch.dev/demo there to click through it.
      <br />
      <a className="hint-link" href="/#banner_section">
        Try Pro free for 7 days
      </a>
      <a className="hint-secondary" href="/#pricing">
        See pricing
      </a>
    </DesktopHint>
  </Mobile>
);
